<template>
  <Card>
    <CardContent class="tw-pt-4">
      <form @submit="onSubmitVacationForm">
        <div class="tw-flex tw-justify-between">
          <CardTitle class="tw-uppercase tw-text-[0.8rem]">
            {{ t('profile.titles.vacation_mode') }}
          </CardTitle>

          <Button type="submit" class="tw-h-[38px]" :disabled="isSubmitting">
            {{ t('profile.titles.submit_vacation') }}
          </Button>
        </div>

        <CardDescription class="tw-mt-0 tw-mb-1">
          {{ t('profile.titles.choose_vacation') }}
        </CardDescription>

        <div class="tw-flex tw-justify-between">
          <div class="tw-w-[45%]">
            <Label>{{ t('profile.titles.start_date') }}</Label>

            <DatePicker
              v-model="startDate"
              name="startDate"
              :placeholder="t('profile.titles.select_start_date')"
              :clearable="false"
              :min-date="minStartDate"
              :max-date="maxStartDate"
              :month-change-on-scroll="false"
              :format="formatVacationDate"
              :preview-format="formatVacationDate"
              :class="{ 'is-invalid': errors.startDate }"
            ></DatePicker>

            <div v-if="errors.startDate" class="tw-text-danger tw-text-[0.7rem] tw-mt-1">
              {{ errors.startDate }}
            </div>
          </div>

          <div class="tw-w-[45%]">
            <Label>{{ t('profile.titles.return_date') }}</Label>

            <DatePicker
              v-model="endDate"
              name="endDate"
              :placeholder="t('profile.titles.select_return_date')"
              :clearable="false"
              :min-date="minEndDate"
              :month-change-on-scroll="false"
              :format="formatVacationDate"
              :preview-format="formatVacationDate"
              :class="{ 'is-invalid': errors.endDate }"
            ></DatePicker>

            <div v-if="errors.endDate" class="tw-text-danger tw-text-[0.7rem] tw-mt-1">
              {{ errors.endDate }}
            </div>
          </div>
        </div>

        <!-- Show replacements if:
                - User is not Admin
                - User is not only a VIEWER
                - User has customer_assignments -->
        <!-- TODO: Remove it for VIEWERs only users too -->
        <div
          v-if="!currentUser?.is_admin && currentUser?.customer_assignments.length"
          class="tw-mt-3"
        >
          <CardDescription class="tw-mt-0 tw-mb-1">
            {{ t('profile.titles.replacements') }}
          </CardDescription>

          <span
            v-if="!customersWithUsersList.length"
            class="tw-text-[0.8rem] tw-underline tw-cursor-pointer tw-text-primary-500"
            :class="{
              'tw-pointer-events-none tw-text-[#9299e0]': isLoadingReplacements,
            }"
            @click="getAvailableReplacementsInDateRange"
          >
            <template v-if="isLoadingReplacements">{{ t('common.data.loading') }}...</template>

            <template v-else>{{ t('profile.titles.see_available_replacements') }}...</template>
          </span>

          <div v-else class="tw-w-full tw-mt-2">
            <div
              v-for="(customerWithUsers, index) in customersWithUsersList"
              :key="customerWithUsers.customerId"
              class="tw-flex tw-mb-2"
            >
              <div
                class="tw-w-1/3 tw-flex tw-items-center tw-bg-[#eef2f7] tw-border tw-border-base-light-gray tw-rounded-l"
              >
                <span class="tw-text-base tw-text-[#969696] tw-px-2">
                  {{ customerWithUsers.customerName }}
                </span>
              </div>

              <div class="tw-w-2/3">
                <SearchableSelect
                  v-model="replacements[index].user"
                  :options="customerWithUsers.availableUsers"
                  value-prop="id"
                  label-prop="display_name"
                  :placeholder="t('profile.titles.choose_replacement')"
                  :class="{ 'tw-border-danger': errors[`replacements[${index}].user`] }"
                />

                <div
                  v-if="
                    errors[`replacements[${index}].user`] ||
                    !customerWithUsers.availableUsers.length
                  "
                  class="tw-text-danger tw-text-[0.7rem] tw-mt-1"
                >
                  <span v-if="!customerWithUsers.availableUsers.length">
                    {{ t('profile.data.no_replacements') }}
                  </span>
                  <span v-else>
                    {{ errors[`replacements[${index}].user`] }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { useUserStore } from '@/stores/user.store';
import { storeToRefs } from 'pinia';
import { watch, ref, type Ref, computed } from 'vue';
import { useField, useFieldArray, useForm } from 'vee-validate';
import { array, object, string } from 'yup';
import { useToast } from 'vue-toast-notification';
import uniqBy from 'lodash/uniqBy';
import { useI18n } from 'vue-i18n';
import { Card, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';

const toast = useToast();
const { t } = useI18n();

const validationSchema = object({
  startDate: string().required().label(t('profile.titles.start_date')),
  endDate: string().required().label(t('profile.titles.end_date')),
  replacements: array().of(
    object({
      customer: string(),
      user: string().required(t('profile.titles.select_associated_user_msg')),
    })
  ),
});

const { handleSubmit, errors, isSubmitting, validate } = useForm({
  validationSchema,
});

const { value: startDate }: { value: Ref<Date> } = useField('startDate');
const { value: endDate }: { value: Ref<Date> } = useField('endDate');
const { value: replacements }: { value: Ref<any> } = useField('replacements');
const { push } = useFieldArray('replacements');

const onSubmitVacationForm = handleSubmit(async values => {
  try {
    if (
      (values.replacements && currentUser.value?.customer_assignments.length) ||
      (!values.replacements && !currentUser.value?.customer_assignments.length)
    ) {
      await updateVacation({
        vacation_start: values.startDate.toISOString().split('T')[0],
        vacation_end: values.endDate.toISOString().split('T')[0],
        replacements: values.replacements,
      });

      toast.open({
        message: t('profile.titles.vacation_dates_submitted_successfully'),
        type: 'success',
      });
    } else {
      toast.open({
        message: t('profile.titles.please_select_replacements_first'),
        type: 'error',
      });
    }
  } catch (error) {
    toast.open({
      message: t('common.errors.something_went_wrong'),
      type: 'error',
    });
  }
});

const isLoadingReplacements: Ref<boolean> = ref(false);
const customersWithUsersList: Ref<
  Array<{ customerId: number; customerName: string; availableUsers: any[] }>
> = ref([]);
const { currentUser } = storeToRefs(useUserStore());
const { getAvailableReplacements, updateVacation } = useUserStore();

const getAvailableReplacementsInDateRange = async () => {
  const { valid } = await validate();

  if (valid) {
    isLoadingReplacements.value = true;
    customersWithUsersList.value = await getCustomerWithUsersList();
    customersWithUsersList.value.forEach(customerWithUsers => {
      push({
        customer: customerWithUsers.customerId,
        user: '',
      });
    });
    isLoadingReplacements.value = false;
  }
};

const getCustomerWithUsersList = async () => {
  const uniqueCustomerAssignments = uniqBy(currentUser.value!.customer_assignments, 'customer');

  const allAvailableUsersList = await Promise.all(
    uniqueCustomerAssignments!.map(customerAssignment => {
      return getAvailableReplacements({
        customer_id: customerAssignment.customer,
        vacation_start: startDate.value.toISOString().split('T')[0], // Use appropriate endpoint format (YYYY-MM-DD)
        vacation_end: endDate.value.toISOString().split('T')[0],
      });
    })
  );

  const customersWithUsers = uniqueCustomerAssignments.map((customerAssignment, index) => {
    return {
      customerId: customerAssignment.customer,
      customerName: customerAssignment.customer_name,
      availableUsers: allAvailableUsersList[index], // it is guaranteed that the order of Promise.all is the same order of uniqueCustomerAssignments
    };
  });

  return customersWithUsers;
};

const formatVacationDate = (date: Date) => {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};

watch(currentUser, async user => {
  if (user) {
    if (currentUser.value?.vacation_start)
      startDate.value = new Date(currentUser.value?.vacation_start!);

    if (currentUser.value?.vacation_end) endDate.value = new Date(currentUser.value?.vacation_end!);
  }
});

const minStartDate = computed(() => {
  return new Date();
});

const maxStartDate = computed(() => {
  return endDate.value;
});

const minEndDate = computed(() => {
  return startDate.value || new Date();
});
</script>

<style scoped>
/* Using Tailwind classes - no additional styles needed */
</style>
